import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Space, Popconfirm, Tooltip } from 'antd';
import { GlobalOutlined, UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import type { ColumnsType } from 'antd/es/table';
import type { AppDispatch, RootState } from '../../../redux/store';
import { fetchMerchants, deleteMerchantDomain } from '../../Merchants/store/merchantsUserSlice';
import type { Merchant } from '../../Merchants/types';
import { ActionIconButton } from '../../Merchants/styles';
import MerchantDomainModal from '../../Merchants/Domain/MerchantDomainModal';
import Table from '../../../commonComponents/Table';
import TablePagination from '../../../commonComponents/Pagination';
import CustomInput from '../../../commonComponents/input';
import { notification } from '../../../commonComponents/Notification/Notification';
import { useTheme } from '../../../context/ThemeContext';
import { SettingsCard, CardHeader, CardTitle, CardTitleText } from '../Styles';
import { CARD_ICON_STYLE } from '../constants';

const AdminDomainSection: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { merchants, loading } = useSelector((state: RootState) => state.merchants);
  const hasFetchedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reviewMerchant, setReviewMerchant] = useState<Merchant | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchMerchants());
    }
  }, [dispatch]);

  const filteredMerchants = useMemo(() => {
    if (!Array.isArray(merchants)) return [];
    if (!searchQuery) return merchants;
    const query = searchQuery.toLowerCase();
    return merchants.filter(
      (merchant) =>
        merchant?.name?.toLowerCase().includes(query) ||
        merchant?.username?.toLowerCase().includes(query) ||
        merchant?.email?.toLowerCase().includes(query)
    );
  }, [merchants, searchQuery]);

  const paginatedMerchants = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredMerchants.slice(startIndex, startIndex + pageSize);
  }, [filteredMerchants, currentPage, pageSize]);

  const handlePageChange = (page: number, size: number) => {
    if (size && size !== pageSize) setPageSize(size);
    if (page !== currentPage) setCurrentPage(page);
  };

  const handleDeleteDomain = async (merchant: Merchant) => {
    setDeletingId(merchant.id);
    try {
      await dispatch(deleteMerchantDomain(merchant.id)).unwrap();
      notification.success('Domain Removed', `${merchant.name}'s domain has been unlinked.`);
    } catch (error: any) {
      notification.error(typeof error === 'string' ? error : error?.message || 'Failed to remove domain.');
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ColumnsType<Merchant> = [
    {
      title: 'MERCHANT',
      key: 'name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar icon={<UserOutlined />} />
          <span style={{ fontWeight: 500 }}>{record.name || record.username || record.email}</span>
        </div>
      ),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || '-',
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <ActionIconButton iconColor="#2D6CCA" onClick={() => setReviewMerchant(record)}>
              <FontAwesomeIcon icon={faEye} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          </Tooltip>
          <Popconfirm
            title="Remove this domain?"
            description="This unlinks the domain from the merchant and removes it from Vercel."
            onConfirm={() => handleDeleteDomain(record)}
            okText="Remove"
            okButtonProps={{ danger: true, loading: deletingId === record.id }}
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <ActionIconButton iconColor="#f5222d" disabled={deletingId === record.id}>
                <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
              </ActionIconButton>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <GlobalOutlined style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Custom Domains</CardTitleText>
        </CardTitle>
      </CardHeader>

      <div style={{ display: 'flex', marginBottom: 16 }}>
        <CustomInput
          placeholder="Search merchants by name, username, or email"
          value={searchQuery}
          onChange={(value: string) => { setSearchQuery(value); setCurrentPage(1); }}
          allowClear
          width="300px"
        />
      </div>

      <Table<Merchant>
        columns={columns}
        dataSource={paginatedMerchants}
        rowKey="id"
        loading={loading.merchants}
        pagination={false}
        scroll={paginatedMerchants.length > 0 ? { x: true } : undefined}
        disableScroll={paginatedMerchants.length === 0}
        emptyText="No merchants found"
        headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
        removeVerticalBorders
        thPadding="15px 10px"
        tdPadding="10px 10px"
        firstChildBorderRadius="8px"
        border="0"
        borderBottomLeftRight
        theme={isDarkMode ? 'dark' : 'light'}
      />

      {filteredMerchants.length > 0 && (
        <TablePagination
          totalCount={filteredMerchants.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePageChange}
          totalLabel="Total merchants"
          padding="16px 0px 0px 0px"
        />
      )}

      <MerchantDomainModal
        open={!!reviewMerchant}
        merchant={reviewMerchant}
        onClose={() => setReviewMerchant(null)}
      />
    </SettingsCard>
  );
};

export default React.memo(AdminDomainSection);
